import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import type { Notification } from './notificationTypes';
import { formatNotificationTime } from './notificationTypes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../ui/utils';

export interface NotificationPanelProps {
  notifications: Notification[];
  unreadCount: number;
  error: boolean;
  onRetry: () => void;
  onMarkAsRead: (id: string) => void;
  onNotificationClick: (n: Notification) => void;
  onMarkAllRead?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode; // trigger (bell button)
}

export function NotificationPanel({
  notifications,
  unreadCount,
  error,
  onRetry,
  onMarkAsRead,
  onNotificationClick,
  onMarkAllRead,
  open,
  onOpenChange,
  children,
}: NotificationPanelProps) {
  const { t } = useTranslation();
  const handleItemClick = (n: Notification) => {
    onMarkAsRead(n.id);
    onNotificationClick(n);
    onOpenChange?.(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[380px] max-w-[calc(100vw-2rem)] p-0 rounded-xl border-[#E2DFEA] dark:border-white/10 bg-white dark:bg-[#16151D] shadow-xl"
      >
        <div className="sticky top-0 bg-white dark:bg-[#16151D] border-b border-[#E2DFEA] dark:border-white/8 px-4 py-3 rounded-t-xl z-10 flex items-center justify-between gap-2">
          <h3
            className="font-semibold text-[#2D2F7E] dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            {t('social.notificationsPanel.title')}
          </h3>
          {!error && unreadCount > 0 && onMarkAllRead && (
            <button
              type="button"
              onClick={() => onMarkAllRead()}
              className="text-xs font-medium text-[#936FEF] hover:underline shrink-0"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('social.notificationsPanel.markAllRead', 'Mark all read')}
            </button>
          )}
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {error && (
            <div className="p-6 text-center">
              <p
                className="text-sm text-[#464452] dark:text-white/70 mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.notificationsPanel.loadError')}
              </p>
              <button
                type="button"
                onClick={onRetry}
                className="text-sm font-medium text-[#936FEF] hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.notificationsPanel.retry')}
              </button>
            </div>
          )}

          {!error && notifications.length === 0 && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F0EFF5] dark:bg-white/10 text-[#936FEF] mb-3">
                <Bell className="w-6 h-6" />
              </div>
              <p
                className="text-[#464452] dark:text-white/70 text-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.notificationsPanel.allCaughtUp')}
              </p>
              <p
                className="text-[#464452] dark:text-white/50 text-xs mt-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.notificationsPanel.allCaughtUpDesc')}
              </p>
            </div>
          )}

          {!error && notifications.length > 0 && (
            <ul className="py-2">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(n)}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F0EFF5] dark:hover:bg-white/5',
                      !n.isRead && 'bg-[#F7F6FA] dark:bg-white/5'
                    )}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0"
                      style={{
                        background:
                          'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                      }}
                    >
                      {n.actorAvatar.startsWith('http') ? (
                        <img
                          src={n.actorAvatar}
                          alt=""
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span>{n.actorAvatar}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          'text-sm',
                          n.isRead
                            ? 'text-[#464452] dark:text-white/70'
                            : 'font-medium text-[#2D2F7E] dark:text-white'
                        )}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {n.message}
                      </p>
                      <p
                        className="text-xs text-[#464452] dark:text-white/50 mt-0.5"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {formatNotificationTime(n.createdAt)}
                      </p>
                    </div>
                    {!n.isRead && (
                      <span
                        className="w-2 h-2 rounded-full bg-[#936FEF] shrink-0 mt-2"
                        aria-hidden
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
